// The shape of each CRM we can connect to, and how someone finds it.
//
// Deliberately free of service imports so it can be tested without axios — the
// same reason recipientRules.ts exists. Types come in as `import type`, which
// the compiler erases, so nothing is required at runtime.
import React from 'react';
import {
  Plug, CalendarClock, Building2, Sparkles, UserPlus, Workflow, ShoppingBag, Webhook,
} from 'lucide-react';
import type { CrmProviderType, CrmAuthType } from 'api/services/crmProviders.service';

/**
 * What each provider needs, and the auth it uses. Driving the form from this
 * rather than showing every field for every vendor is the difference between a
 * setup someone can complete and one they guess at — Zoho needs a refresh token
 * and a datacentre, Zenoti an API key and a centre id, and they share nothing.
 */
export const SHAPES: Record<CrmProviderType, {
  label: string;
  /** Shows the lead field-mapping editor. Only Zoho supports it today. */
  fieldMapping?: boolean;
  /**
   * Configured by its own form (CustomApiForm) instead of the shared fields, and
   * never the organisation's lead/booking CRM — the server stores supportsLeads /
   * supportsBooking false whatever is sent, so those boxes are not offered.
   */
  customApi?: boolean;
  /** Other names people type. "CatchMyCall" was unfindable when the label read
   *  only "Built-in booking" — the product's own name matched nothing. */
  aliases?: string[];
  icon: React.ComponentType<{ className?: string }>;
  blurb: string;
  auth: CrmAuthType;
  surfaces: { booking: boolean; leads: boolean };
  needs: { accountsServer?: string; apiDomain?: string; clientId?: string; clientSecret?: boolean; secret?: string; config?: string };
}> = {
  ZOHO_CRM: {
    icon: Building2,
    aliases: ['zoho', 'crm', 'leads'],
    label: 'Zoho CRM',
    blurb: 'Leads, contacts, calls and notes. Every AI call is logged against the caller.',
    auth: 'OAUTH2_REFRESH_TOKEN',
    surfaces: { booking: false, leads: true },
    // Field mapping. Optional — without it the standard five fields are sent,
    // which is what every Zoho accepts.
    fieldMapping: true,
    needs: {
      accountsServer: 'https://accounts.zoho.in',
      clientId: 'Client ID', clientSecret: true, secret: 'Refresh token',
    },
  },
  ZOHO_BOOKINGS: {
    icon: CalendarClock,
    aliases: ['zoho', 'appointments', 'calendar'],
    label: 'Zoho Bookings',
    blurb: 'Services, availability and appointments. Use with Zoho CRM for the full picture.',
    auth: 'OAUTH2_REFRESH_TOKEN',
    surfaces: { booking: true, leads: false },
    needs: {
      accountsServer: 'https://accounts.zoho.in',
      clientId: 'Client ID', clientSecret: true, secret: 'Refresh token',
    },
  },
  ZENOTI: {
    icon: Sparkles,
    aliases: ['salon', 'spa', 'zenoti'],
    label: 'Zenoti',
    blurb: 'Used by larger salon and spa chains. Bookings are held then confirmed.',
    auth: 'API_KEY',
    surfaces: { booking: true, leads: false },
    needs: {
      apiDomain: 'https://api.zenoti.com',
      secret: 'API key',
      config: '{"center_id":"paste the centre GUID from Zenoti"}',
    },
  },
  LEADSQUARED: {
    icon: UserPlus,
    aliases: ['leadsquared', 'leads', 'clinic'],
    label: 'LeadSquared',
    blurb: 'Lead capture and follow-up. Common in derma clinics alongside a booking system.',
    auth: 'ACCESS_SECRET',
    surfaces: { booking: false, leads: true },
    needs: {
      apiDomain: 'https://api-in21.leadsquared.com',
      clientId: 'Access key', secret: 'Secret key',
    },
  },
  CATCHMYCALL: {
    icon: Plug,
    aliases: ['catchmycall', 'catch my call', 'built in', 'builtin', 'no crm', 'none'],
    label: 'CatchMyCall booking (built-in)',
    blurb: 'For businesses with no CRM. Set up your services and opening hours and the agent books directly.',
    auth: 'NONE',
    surfaces: { booking: true, leads: false },
    needs: {},
  },
  SHOPIFY: {
    icon: ShoppingBag,
    aliases: ['shopify', 'shop', 'store', 'ecommerce', 'e-commerce', 'd2c', 'orders', 'customers'],
    label: 'Shopify',
    blurb: 'For a brand selling online. Callers are matched to your Shopify customers, and calls land on their record.',
    auth: 'API_KEY',
    surfaces: { booking: false, leads: true },
    needs: {
      apiDomain: 'mystore.myshopify.com',
      secret: 'Admin API access token',
      accountsServer: '2024-10',
    },
  },
  N8N: {
    icon: Workflow,
    aliases: ['n8n', 'workflow', 'miosalon', 'dingg', 'practo', 'google sheets', 'calendar', 'outlook', 'zapier'],
    label: 'n8n workflow',
    blurb: 'Anything else — MioSalon, DINGG, Practo, Google Calendar, Outlook, even a Google Sheet.',
    auth: 'API_KEY',
    surfaces: { booking: true, leads: true },
    needs: {
      secret: 'Webhook secret',
      config: '{"webhook_base":"https://n8n.example.com/webhook/your-salon","timeout_ms":4000}',
    },
  },
  CUSTOM_API: {
    icon: Webhook,
    aliases: ['custom', 'api', 'http', 'rest', 'webhook', 'push', 'fetch', 'lookup', 'form', 'xml', 'yamurai'],
    label: 'Custom API',
    blurb: 'Push a captured lead or form to any HTTP API, or let the agent look something up mid-call. Bound per agent, beside your CRM.',
    auth: 'NONE',
    surfaces: { booking: false, leads: false },
    customApi: true,
    needs: {},
  },
};

export const ORDER: CrmProviderType[] =
  ['CATCHMYCALL', 'ZOHO_CRM', 'ZOHO_BOOKINGS', 'ZENOTI', 'LEADSQUARED', 'SHOPIFY', 'N8N', 'CUSTOM_API'];

/**
 * Matches the label, the type and the aliases — deliberately NOT the blurb.
 *
 * Zoho Bookings' blurb reads "Use with Zoho CRM for the full picture", so
 * including blurbs made a search for "zoho crm" return Bookings too. A blurb
 * that name-drops another product should not make this one match that name.
 */
export const matchesQuery = (t: CrmProviderType, q: string): boolean => {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  const sh = SHAPES[t];
  const hay = [t, sh.label, ...(sh.aliases || [])].join(' ').toLowerCase();
  return needle.split(/\s+/).every(w => hay.includes(w));
};
